import dayjs from '~/timeInit';

export type SabreFlightSegment = {
  segmentNumber: number;
  airline: string;
  airlineCode: string;
  flightNumber: string;
  flightNumberOnly: string;
  bookingClass: string;
  departureDate: string; // Format: dd/mm/yyyy
  departureAirport: string;
  departureAirportName?: string; // Full airport/city name from lookup
  arrivalAirport: string;
  arrivalAirportName?: string; // Full airport/city name from lookup
  departureTime: string;
  arrivalTime: string;
  arrivalDate: string; // Format: dd/mm/yyyy (defaults to departureDate if not specified)
  status: string;
  passengers: number;
  confirmationCode?: string;
  airlinePnr?: string; // Airline-specific PNR extracted from /DC format (e.g., MF6XWW from /DCEK*MF6XWW)
};

export type SabrePassenger = {
  firstName: string;
  middleName?: string;
  lastName: string;
  type: 'adult' | 'child' | 'infant';
};

export type SabreParsedData = {
  pnr: string;
  name: string;
  flightType: 'one-way' | 'return' | 'multi-city';
  passengers: Array<SabrePassenger>;
  segments: Array<SabreFlightSegment>;
  legs: Array<Array<SabreFlightSegment>>;
};

export type AirportLookup = Record<string, string>;
export type AirlineLookup = Record<string, string>;

/**
 * Convert Sabre date format (02FEB) to dd/mm/yyyy format
 * Uses smart year detection: flights cannot be more than a year in advance
 * If flight month is before current month, assume next year
 */
const convertSabreDate = (sabreDate: string): string => {
  if (!sabreDate) return '';

  const monthMap: Record<string, number> = {
    JAN: 1,
    FEB: 2,
    MAR: 3,
    APR: 4,
    MAY: 5,
    JUN: 6,
    JUL: 7,
    AUG: 8,
    SEP: 9,
    OCT: 10,
    NOV: 11,
    DEC: 12,
  };

  const day = parseInt(sabreDate.substring(0, 2), 10);
  const monthAbbr = sabreDate.substring(2, 5).toUpperCase();
  const flightMonth = monthMap[monthAbbr];

  if (!flightMonth) {
    return sabreDate;
  }

  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month() + 1; // month() returns 0-11

  // If flight month is before current month, it must be next year
  // (flights can't be in the past or more than a year in advance)
  let year = currentYear;
  if (flightMonth < currentMonth) {
    year = currentYear + 1;
  }

  const flightDate = dayjs()
    .year(year)
    .month(flightMonth - 1)
    .date(day);
  const formattedDate = flightDate.format('DD/MM/YYYY');

  return formattedDate;
};

/**
 * Calculate days between two dates in dd/mm/yyyy format
 */
const daysBetween = (date1Str: string, date2Str: string): number => {
  if (!date1Str || !date2Str) return 999; // Return large number if dates can't be parsed

  const date1 = dayjs(date1Str, 'DD/MM/YYYY');
  const date2 = dayjs(date2Str, 'DD/MM/YYYY');

  if (!date1.isValid() || !date2.isValid()) {
    return 999; // Return large number if dates can't be parsed
  }

  return date2.diff(date1, 'day');
};

/**
 * Format time from HHMM to HH:MM
 */
const formatTime = (timeStr: string): string => {
  if (!timeStr || timeStr.length !== 4) {
    return timeStr;
  }
  return `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}`;
};

/**
 * Extract confirmation code and airline PNR from a line of text
 * Supports /DC (Direct Connect) and /ND prefixes, with * or - separators
 * Examples: /DCEK*MF6XWW, /NDSQ*FVUHY7, /SQ-FVUHY7
 * @returns Object with confirmationCode and airlinePnr (empty strings if not found)
 */
export const extractConfirmationAndPnr = (line: string): { confirmationCode: string; airlinePnr: string } => {
  const confirmMatch = line.match(/\/(?:DC|ND)([A-Z0-9*-]+)/);
  if (!confirmMatch) {
    return { confirmationCode: '', airlinePnr: '' };
  }

  const confirmationCode = confirmMatch[1];
  const pnrParts = confirmationCode.split(/[*-]/);
  const airlinePnr = pnrParts.length > 1 ? pnrParts[1] : '';

  return { confirmationCode, airlinePnr };
};

/**
 * Determine if segments form a single leg with connections vs separate legs
 * Groups segments that are on the same day or next day with reasonable connection times
 */
export const groupFlightSegments = (segments: Array<SabreFlightSegment>): Array<Array<SabreFlightSegment>> => {
  if (segments.length === 0) {
    return [];
  }

  const legs: Array<Array<SabreFlightSegment>> = [];
  let currentLeg: Array<SabreFlightSegment> = [segments[0]];

  for (let i = 1; i < segments.length; i++) {
    const prevSegment = segments[i - 1];
    const currentSegment = segments[i];

    // Use arrival date for previous segment
    const prevArrivalDate = prevSegment.arrivalDate;
    const currentDepDate = currentSegment.departureDate;

    // Check connection criteria
    const isSameAirport = prevSegment.arrivalAirport === currentSegment.departureAirport;
    const daysDiff = daysBetween(prevArrivalDate, currentDepDate);
    const isWithin24Hours = daysDiff >= 0 && daysDiff <= 1; // Same day or next day only

    // Check if this is a return flight pattern (airports are flipped)
    // e.g., MEL->SYD followed by SYD->MEL indicates a return, not a connection
    const isReturnFlight =
      prevSegment.departureAirport === currentSegment.arrivalAirport &&
      prevSegment.arrivalAirport === currentSegment.departureAirport;

    // It's a connection if:
    // 1. Same airport AND within 24 hours AND not a return flight pattern
    const isConnection = isSameAirport && isWithin24Hours && !isReturnFlight;

    if (isConnection) {
      currentLeg.push(currentSegment);
    } else {
      // Start new leg
      legs.push(currentLeg);
      currentLeg = [currentSegment];
    }
  }

  if (currentLeg.length > 0) {
    legs.push(currentLeg);
  }

  return legs;
};

/**
 * Determine flight type based on leg structure
 */
export const determineFlightType = (
  segments: Array<SabreFlightSegment>,
  legs: Array<Array<SabreFlightSegment>>,
): 'one-way' | 'return' | 'multi-city' => {
  if (segments.length === 0 || legs.length === 0) {
    return 'one-way';
  }

  // Get first and last segment
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];

  const originCity = firstSegment.departureAirport;
  const finalDestination = lastSegment.arrivalAirport;

  // One-way: Single leg or origin != final destination with only outbound leg
  if (legs.length === 1) {
    return 'one-way';
  }

  // Return: Two legs where origin = final destination
  if (legs.length === 2 && originCity === finalDestination) {
    const outboundDest = legs[0][legs[0].length - 1].arrivalAirport;
    const returnOrigin = legs[1][0].departureAirport;

    // Check if it's a proper return (outbound destination matches return origin)
    if (outboundDest === returnOrigin) {
      return 'return';
    }
  }

  // Multi-city: More than 2 legs, or origin != final destination with multiple legs
  return 'multi-city';
};

/**
 * Format airport code with name if available
 * @param code - Airport IATA code (e.g., "MEL")
 * @param airportLookup - Optional lookup map from code to name
 * @returns Formatted string like "Melbourne (MEL)" or just "MEL" if name not found
 */
const formatAirportDisplay = (code: string, airportLookup?: AirportLookup): string => {
  if (!airportLookup || !airportLookup[code]) {
    return code;
  }
  return `${airportLookup[code]} (${code})`;
};

/**
 * Generates a human-readable itinerary name from flight legs
 * @param legs - Array of flight legs (each leg is an array of connected segments)
 * @param airportLookup - Optional mapping of airport codes to city names
 * @returns Formatted itinerary string (e.g., "Melbourne (MEL) -> Dubai (DXB), Dubai (DXB) -> Prague (PRG)")
 * @example
 * const legs = [[{ departureAirport: 'MEL', arrivalAirport: 'DXB', ... }]];
 * const lookup = { MEL: 'Melbourne', DXB: 'Dubai' };
 * generateItineraryName(legs, lookup); // Returns: "Melbourne (MEL) -> Dubai (DXB)"
 */
export const generateItineraryName = (
  legs: Array<Array<SabreFlightSegment>>,
  airportLookup?: AirportLookup,
): string => {
  return legs
    .map((leg) => {
      const firstSegment = leg[0];
      const lastSegment = leg[leg.length - 1];
      const departure = formatAirportDisplay(firstSegment.departureAirport, airportLookup);
      const arrival = formatAirportDisplay(lastSegment.arrivalAirport, airportLookup);
      return `${departure} -> ${arrival}`;
    })
    .join(', ');
};

/**
 * Parse Sabre GDS flight booking data
 * Refer to README.md for more details on the format.
 * @param sabreText - Raw Sabre GDS text
 * @param airportLookup - Optional lookup map from airport code to name
 */
export const parseSabreGDS = (
  sabreText: string,
  airportLookup?: AirportLookup,
  airlineLookup?: AirlineLookup,
): SabreParsedData | null => {
  try {
    const lines = sabreText.split('\n');

    // Extract PNR
    let pnr = '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed && !trimmed.match(/^\d+\.\d+[A-Z]/)) {
        pnr = trimmed;
        break;
      }
    }

    // Extract passengers
    const passengers: Array<SabrePassenger> = [];
    const passengerRegex = /(\d+)\.(\d+)([A-Z]+)\/([A-Z\s]+?)\s+(MR|MS|MRS|MISS|MSTR|DR|PROF)(?=\s+\d+\.|$)/g;

    for (const line of lines) {
      let match;
      // Reset regex lastIndex for each line
      passengerRegex.lastIndex = 0;
      while ((match = passengerRegex.exec(line)) !== null) {
        const lastName = match[3];
        const firstMiddleNames = match[4].trim().split(/\s+/);
        const title = match[5];

        const passenger: SabrePassenger = {
          firstName: firstMiddleNames[0] || '',
          middleName: firstMiddleNames.length > 1 ? firstMiddleNames.slice(1).join(' ') : undefined,
          lastName: lastName,
          type: title === 'MSTR' ? 'child' : 'adult',
        };

        passengers.push(passenger);
      }
    }

    // Extract flight segments
    const segments: Array<SabreFlightSegment> = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Skip ARNK and OPERATED BY lines
      if (line.includes('ARNK') || line.includes('OPERATED BY')) {
        continue;
      }

      // Regex for flight line with multiple spaces
      // Note: Flight numbers can be 1-4 digits (e.g., QF 8, EK 405, KL1356)
      // Booking class is the single letter immediately after the flight number (case-insensitive in source PNRs)
      // Day-of-week can be numeric (1-7) or letter (M=Mon, T=Tue, W=Wed, Q=Thu, F=Fri, J=Sat, S=Sun)
      const flightRegex =
        /^\s*(\d+)\s+([A-Z0-9]{2})\s*(\d{1,4})\s*([A-Za-z])?\s+(\d{2}[A-Z]{3})\s+[A-Z0-9]+\s+([A-Z]{3})([A-Z]{3})[A-Z]*\s*\*?\s*([A-Z]{2}\d+)\s+(\d{4})\s+(\d{4})/;
      const match = line.match(flightRegex);

      if (match) {
        const [, segNum, airline, flightNum, bookingClass, depDate, depAirport, arrAirport, status, depTime, arrTime] =
          match;

        // Look for arrival date on the same line (format: 03MAY 7 HRS or 16MAY 6)
        // HRS suffix is optional; day-of-week can be numeric (1-7) or letter (M,T,W,Q,F,J,S)
        let arrivalDateRaw = '';
        const arrivalDateMatch = line.match(/\d{4}\s+(\d{2}[A-Z]{3})\s+[A-Z0-9]+(?:\s+HRS)?/);
        if (arrivalDateMatch) {
          arrivalDateRaw = arrivalDateMatch[1];
        }

        // Look for confirmation code on this line or next line
        let { confirmationCode, airlinePnr } = extractConfirmationAndPnr(line);
        if (!confirmationCode && i + 1 < lines.length) {
          // Check next line for confirmation code
          ({ confirmationCode, airlinePnr } = extractConfirmationAndPnr(lines[i + 1]));
        }

        const formattedDepDate = convertSabreDate(depDate);
        const formattedArrDate = arrivalDateRaw ? convertSabreDate(arrivalDateRaw) : formattedDepDate;
        const airlineName = airlineLookup?.[airline] || airline;

        // Look up airport names from the provided lookup
        const departureAirportName = airportLookup?.[depAirport];
        const arrivalAirportName = airportLookup?.[arrAirport];

        const segment: SabreFlightSegment = {
          segmentNumber: parseInt(segNum),
          airline: airlineName,
          airlineCode: airline,
          flightNumber: `${airline}${flightNum}`,
          flightNumberOnly: flightNum,
          bookingClass: bookingClass || '',
          departureDate: formattedDepDate,
          departureAirport: depAirport,
          departureAirportName,
          arrivalAirport: arrAirport,
          arrivalAirportName,
          departureTime: formatTime(depTime),
          arrivalTime: formatTime(arrTime),
          arrivalDate: formattedArrDate,
          status,
          passengers: parseInt(status.replace(/[A-Z]/g, '')),
          confirmationCode,
          airlinePnr,
        };

        segments.push(segment);
      }
    }

    const legs = groupFlightSegments(segments);
    const flightType = determineFlightType(segments, legs);
    const name = generateItineraryName(legs, airportLookup);

    const result: SabreParsedData = {
      pnr,
      name,
      flightType,
      passengers,
      segments,
      legs,
    };

    return result;
  } catch (error) {
    return null;
  }
};
